import axios from "axios";
import { ActiveFilter } from "../hooks/useFilters";
import { cleanEmptyFilters } from "../hooks/ideaPage/filterHelpers";
import {EvidenceInclusion} from "../components/ideas/store/types";

const API_DOMAIN = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN;

// Get Single Idea
export const getIdeaById = async (id: string, auth0: any): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/${id}`;
    const response = await axios.get(url);
    return response.data.idea;
  } catch (e: any) {
    console.error("Error getting idea:", e);
    throw e;
  }
};

// Get Product Status History for Trend Graph
export const getProductStatusHistory = async (id: string, auth0: any): Promise<any[]> => {
  try {
    const url = `${API_DOMAIN}/idea/product-status-history/${id}`;
    const response = await axios.get(url);
    return response.data.statusHistory || [];
  } catch (e: any) {
    console.error("Error getting product status history:", e);
    return [];
  }
};

// Update Idea
export const updateIdea = async (idea: any, auth0: any): Promise<{ success: boolean }> => {
  try {
    const url = `${API_DOMAIN}/idea/update`;
    const response = await axios.post(url, { idea });
    return response.data;
  } catch (e: any) {
    console.error("Error updating idea:", e);
    throw e;
  }
};

// Star/Unstar Idea
export const toggleIdeaStar = async (
  ideaId: string,
  push: boolean,
  auth0: any
): Promise<{ starred: string; success: boolean; result: any }> => {
  try {
    const url = `${API_DOMAIN}/idea/starred`;
    const response = await axios.post(
      url,
      { idea: { id: ideaId, push } });
    return response.data;
  } catch (e: any) {
    console.error("Error toggling idea star:", e);
    throw e;
  }
};

// Cache for idea config to prevent repeated API calls
let cachedIdeaConfig: any = null;
let configCacheTimestamp = 0;
const CONFIG_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

export const getIdeaConfig = async (
  auth0: any,
): Promise<any> => {
  const now = Date.now();
  
  // Return cached config if still valid
  if (cachedIdeaConfig && (now - configCacheTimestamp) < CONFIG_CACHE_DURATION_MS) {
    return cachedIdeaConfig;
  }
  
  try {
    const url = `${API_DOMAIN}/idea/config`;
    const response = await axios.get(url);

    // Cache the response
    cachedIdeaConfig = response.data;
    configCacheTimestamp = now;

    return response.data;
  } catch (e: any) {
    console.error("Error getting idea config:", e);
    throw e;
  }
};

// Clear idea config cache (useful after config updates)
export const clearIdeaConfigCache = () => {
  cachedIdeaConfig = null;
  configCacheTimestamp = 0;
};

// Get specific configuration by key
export const getIdeaConfigByKey = async (
  configKey: string,
  auth0: any,
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/config/${configKey}`;
    const response = await axios.get(url);

    return response.data;
  } catch (e: any) {
    console.error(`Error getting ${configKey} config:`, e);
    throw e;
  }
};

// Specific configuration getters
export const getPerspectiveConfigurations = async (auth0: any): Promise<any> => {
  return getIdeaConfigByKey('perspectives', auth0);
};

export const getImpactAggregationProperties = async (auth0: any): Promise<any> => {
  return getIdeaConfigByKey('impactProperties', auth0);
};

/** Aggregation keys for get_aggregations API, derived from idea impact config (impactAggregationProperties[].propertyName). */
export function getAggregationKeysFromImpactConfig(impactConfig: any): string[] {
  const props = impactConfig?.impactAggregationProperties;
  if (!Array.isArray(props)) return [];
  const keys = props
    .map((p: { propertyName?: string }) => p?.propertyName)
    .filter((k): k is string => typeof k === 'string' && k.length > 0);
  return [...new Set(keys)];
}

export const getIdeaInfoTabsConfig = async (auth0: any): Promise<any> => {
  return getIdeaConfigByKey('infoTabs', auth0);
};

export const getIdeaColumns = async (auth0: any): Promise<any> => {
  return getIdeaConfigByKey('columns', auth0);
};

export const getSubPages = async (auth0: any): Promise<any> => {
  return getIdeaConfigByKey('subPages', auth0);
};

export const getTabsConfig = async (auth0: any): Promise<any> => {
  return getIdeaConfigByKey('tabs', auth0);
};

// Calculate impact aggregations with optional filters
export const calculateIdeaImpact = async (
  ideaId: string,
  activeFilters: ActiveFilter[],
  auth0: any,
  evidenceInclusion: EvidenceInclusion = 'total'
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/impact`;
    
    // Clean empty filters before sending to API
    const cleanedFilters = cleanEmptyFilters(activeFilters || []);
    
    const response = await axios.post(url, {
      filters: { activeFilters: cleanedFilters },
      evidenceInclusion
    });

    return response.data;
  } catch (e: any) {
    console.error("Error calculating idea impact:", e);
    throw e;
  }
};

export const getIdeaFilteredEvidences = async (
  activeFilters: ActiveFilter[],
  ideaId: string,
  auth0: any,
  search?: string,
  sort?: {
    field?: string;
    direction?: 'asc' | 'desc';
  },
  evidenceInclusion: EvidenceInclusion = 'total'
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/evidences/filters_query`;

    // Clean empty filters before sending to API
    const cleanedFilters = cleanEmptyFilters(activeFilters || []);
    
    // Prepare request body with cleaned filters, search, sort, and evidence inclusion
    const requestBody: any = {
      filters: { 
        activeFilters: cleanedFilters, 
        search: search || '' 
      },
      evidenceInclusion: evidenceInclusion
    };

    // Add sorting if provided
    if (sort && sort.field) {
      requestBody.sort = {
        [sort.field]: sort.direction === 'asc' ? 1 : -1
      };
    }

    const response = await axios.post(
      url,
      requestBody);

    // The API already returns evidences and suggestedEvidences separately
    // No need to modify the status here as the API handles this properly
    return response.data;
  } catch (e: any) {
    console.error("Error getting filtered evidences:", e);
    throw e;
  }
};

// New separate API functions for each data source
export const getAssignedEvidences = async (
  ideaId: string,
  activeFilters: ActiveFilter[],
  trendFilters: ActiveFilter[],
  auth0: any,
  sort?: {
    field?: string;
    direction?: 'asc' | 'desc';
  },
  pagination?: {
    limit: number;
    skip: number;
  }
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/evidences/assigned`;

    const cleanedFilters = cleanEmptyFilters([...activeFilters, ...trendFilters] || []);

    const requestBody: any = {
      filters: {
        activeFilters: cleanedFilters,
      },
    };

    if (sort && sort.field) {
      requestBody.sort = {
        [sort.field]: sort.direction === 'asc' ? 1 : -1,
      };
    }

    if (pagination) {
      requestBody.limit = pagination.limit;
      requestBody.skip = pagination.skip;
    }

    const response = await axios.post(url, requestBody);
    return response.data;
  } catch (e: any) {
    console.error("Error getting assigned evidences:", e);
    throw e;
  }
};

export const getTabEvidenceTotals = async (
  ideaId: string,
  activeFilters: ActiveFilter[],
  trendFilters: ActiveFilter[],
  auth0: any,
  evidenceInclusion = 'total',
  sort?: {
    field?: string;
    direction?: 'asc' | 'desc';
  }
): Promise<{
  countsByTab: Record<string, {
    totalCount: number;
    sections: Array<{ label: string; dataSource: string; count: number }>;
  }>;
}> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/evidences/tab-totals`;
    const cleanedBusinessFilters = cleanEmptyFilters(activeFilters || []);
    const cleanedTrendFilters = cleanEmptyFilters(trendFilters || []);

    const requestBody: any = {
      filters: {
        activeFilters: cleanedBusinessFilters,
        trendFilters: cleanedTrendFilters,
      },
      evidenceInclusion,
    };

    if (sort?.field) {
      requestBody.sort = {
        [sort.field]: sort.direction === 'asc' ? 1 : -1,
      };
    }

    const response = await axios.post(url, requestBody);
    return response.data;
  } catch (e: any) {
    console.error('Error getting tab evidence totals:', e);
    throw e;
  }
};

export const getSuggestedEvidences = async (
  ideaId: string,
  activeFilters: ActiveFilter[],
  trendFilters: ActiveFilter[],
  auth0: any,
  sort?: {
    field?: string;
    direction?: 'asc' | 'desc';
  },
  pagination?: {
    limit: number;
    skip: number;
  }
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/evidences/suggested`;

    const cleanedFilters = cleanEmptyFilters([...activeFilters, ...trendFilters] || []);

    const requestBody: any = {
      filters: {
        activeFilters: cleanedFilters,
      },
    };

    if (sort && sort.field) {
      requestBody.sort = {
        [sort.field]: sort.direction === 'asc' ? 1 : -1,
      };
    }

    if (pagination) {
      requestBody.limit = pagination.limit;
      requestBody.skip = pagination.skip;
    }

    const response = await axios.post(url, requestBody);
    return response.data;
  } catch (e: any) {
    console.error("Error getting suggested evidences:", e);
    throw e;
  }
};

export const getSuggestedEvidenceIds = async (
  ideaId: string,
  auth0: any,
  options: { strongMatchesOnly?: boolean; activeFilters?: any[] } = {}
): Promise<{ evidenceIds: string[] }> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/evidences/suggested-ids`;
    const response = await axios.post(
      url,
      {
        strongMatchesOnly: options.strongMatchesOnly ?? true,
        activeFilters: options.activeFilters ?? [],
      });
    return response.data;
  } catch (e: any) {
    console.error("Error getting suggested evidence IDs:", e);
    throw e;
  }
};

export const getAdditionalEvidences = async (
  ideaId: string,
  activeFilters: ActiveFilter[],
  trendFilters: ActiveFilter[],
  search: string,
  auth0: any,
  sort?: {
    field?: string;
    direction?: 'asc' | 'desc';
  }
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/evidences/additional`;

    // Clean empty filters before sending to API
    const cleanedFilters = cleanEmptyFilters([...activeFilters, ...trendFilters] || []);
    
    // Prepare request body
    const requestBody: any = {
      filters: { 
        activeFilters: cleanedFilters,
        search: search || ''
      }
    };

    // Add sorting if provided
    if (sort && sort.field) {
      requestBody.sort = {
        [sort.field]: sort.direction === 'asc' ? 1 : -1
      };
    }

    const response = await axios.post(url, requestBody);
    return response.data;
  } catch (e: any) {
    console.error("Error getting additional evidences:", e);
    throw e;
  }
};

export const getSubThemes = async (ideaId: string, auth0: any, evidenceIds: string[], activeFilters: string): Promise<any> => {
  try {
    const url = window.__RUNTIME_CONFIG__.REACT_APP_MLAPI_DOMAIN + `/sub_themes`;
    const response = await axios.post(url, {"ideaId": ideaId, evidenceIds: evidenceIds, filtersKey: activeFilters});
  
    return response.data;
  } catch (e: any) {
    console.error("Error getting sub themes:", e);
    throw e;
  }
};

const fetchEvidencesAggregations = async (
  evidenceIdsLists: string[][],
  auth0: any,
  aggregations?: string[]
): Promise<any> => {
  const url = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + `/ai-agent/evidences/get_aggregations`;
  let aggKeys = aggregations;
  if (!aggKeys?.length) {
    const impactConfig = await getImpactAggregationProperties(auth0);
    aggKeys = getAggregationKeysFromImpactConfig(impactConfig);
  }
  const response = await axios.post(
    url,
    { evidence_ids_lists: evidenceIdsLists, aggregations: aggKeys });
  return response.data;
};

export const getEvidencesAggregations = async (
  evidence_ids_lists: string[],
  auth0: any,
  aggregations?: string[]
): Promise<any> => {
  try {
    return await fetchEvidencesAggregations([evidence_ids_lists], auth0, aggregations);
  } catch (e: any) {
    console.error("Error getting evidences aggregations:", e);
    throw e;
  }
};

export const getThemesBatchAggregations = async (
  evidenceIdLists: string[][],
  auth0: any,
  aggregations?: string[]
): Promise<any> => {
  try {
    if (!evidenceIdLists.length) {
      return { aggregations: [] };
    }
    return await fetchEvidencesAggregations(evidenceIdLists, auth0, aggregations);
  } catch (e: any) {
    console.error("Error getting batch themes aggregations:", e);
    throw e;
  }
};

// Get perspective counts for all perspectives in a single API call
export const getPerspectiveCounts = async (
  ideaId: string,
  currentPerspective: string,
  activeFilters: ActiveFilter[],
  perspectiveConfigurations: any[],
  auth0: any
): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/idea/${ideaId}/perspective-counts`;
    
    const response = await axios.post(url, {
      currentPerspective,
      activeFilters: cleanEmptyFilters(activeFilters),
      perspectiveConfigurations
    });

    return response.data;
  } catch (e: any) {
    console.error("Error getting perspective counts:", e);
    throw e;
  }
};

const inFlightPerspectiveCountRequests = new Map<string, Promise<any>>();
const completedPerspectiveCountCache = new Map<string, { data: any; expiresAt: number }>();
const PERSPECTIVE_COUNT_CACHE_TTL_MS = 5 * 60 * 1000;

const buildPerspectiveCountRequestKey = (
  ideaId: string,
  fieldPath: string,
  activeFilters: ActiveFilter[],
  trendRange?: string
): string => {
  const cleanedFilters = cleanEmptyFilters(activeFilters);
  const filtersSignature = JSON.stringify(
    cleanedFilters
      .map((filter) => ({
        name: filter.name,
        selected: Array.isArray(filter.selected)
          ? [...filter.selected].map(String).sort()
          : filter.selected,
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  );

  return `${ideaId}:${fieldPath}:${trendRange || ''}:${filtersSignature}`;
};

// Get counts for a single perspective field with trend analysis
export const getSinglePerspectiveCount = async (
  ideaId: string,
  fieldPath: string,
  activeFilters: ActiveFilter[],
  trendRange?: string,
  auth0?: any
): Promise<any> => {
  const requestKey = buildPerspectiveCountRequestKey(ideaId, fieldPath, activeFilters, trendRange);
  const cached = completedPerspectiveCountCache.get(requestKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }

  const existingRequest = inFlightPerspectiveCountRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const requestPromise = (async () => {
    try {
      const url = `${API_DOMAIN}/idea/${ideaId}/perspective-count`;
      const requestBody: any = {
        fieldPath,
        activeFilters: cleanEmptyFilters(activeFilters),
      };

      if (trendRange) {
        requestBody.trendRange = trendRange;
      }

      const response = await axios.post(url, requestBody);
      const data = response.data;
      completedPerspectiveCountCache.set(requestKey, {
        data,
        expiresAt: Date.now() + PERSPECTIVE_COUNT_CACHE_TTL_MS,
      });
      return data;
    } catch (e: any) {
      console.error("Error getting single perspective count:", e);
      throw e;
    } finally {
      inFlightPerspectiveCountRequests.delete(requestKey);
    }
  })();

  inFlightPerspectiveCountRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const getCustomTimeRangeCount = async (
  ideaId: string,
  activeFilters: ActiveFilter[],
  auth0?: any
): Promise<any> => {
  try {

    const url = `${API_DOMAIN}/idea/${ideaId}/perspective-count-custom`;

    const requestBody: any = {
  
      activeFilters: cleanEmptyFilters(activeFilters)
    };
  
    const response = await axios.post(url, requestBody);

    return response.data;
  } catch (e: any) {
    console.error("Error getting single perspective count:", e);
    throw e;
  }
};

export const ideaValidation = (
  auth0: any,
  idea_id?: string
) => {
  return new Promise<any>(async (resolve, reject) => {
    try {
      const url = window.__RUNTIME_CONFIG__.REACT_APP_MLAPI_DOMAIN + `/idea_validation/${idea_id}`;
      const response = await axios.post(url, {"max": "1"});
      resolve(response);
    } catch (e: any) {
      console.log(e);
      reject(e);
    }
  });
};

export const getIdeaPages = async (auth0: any): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/ideas/pages`;
    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error("Error getting idea pages:", e);
    throw e;
  }
};

export const getIdeaPageByKey = async (auth0: any, key: string): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/ideas/pages/${key}`;
    const response = await axios.get(url);
    return response.data;
  } catch (e: any) {
    console.error("Error getting idea page by key:", e);
    throw e;
  }
};

export const cloneIdeaPage = async (auth0: any, body: any): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/ideas/pages/add`;
    const requestBody: any = {
      ...body,
      isClone: true
    }
    const response = await axios.post(url, requestBody);

    return response.data;
  } catch (e: any) {
    console.error("Error on clone idea page:", e);
    throw e;
  }
};

export const editIdeaPage = async (auth0: any, body: any): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/ideas/pages/add`;
    const response = await axios.post(url, body);

    return response.data;
  } catch (e: any) {
    console.error("Error on update idea page:", e);
    throw e;
  }
};

export const deleteIdeaPage = async (auth0: any, body: any): Promise<any> => {
  try {
    const url = `${API_DOMAIN}/ideas/pages/${body.key}`;
    const response = await axios.delete(url);

    return response.data;
  } catch (e: any) {
    console.error("Error on delete idea page:", e);
    throw e;
  }
};

export interface AvailableColumnSourceItem {
  accessor: string;
  defaultTitle: string;
  dataType: string;
  renderComponent: string;
  source: string;
}

export interface AvailableColumnSourcesResponse {
  basicIdeaFields: AvailableColumnSourceItem[];
  mappedIdeaFields: AvailableColumnSourceItem[];
  productItemFields: AvailableColumnSourceItem[];
  properties: AvailableColumnSourceItem[];
}

export const getAvailableColumnSources = async (
  pageKey: string,
  auth0: any
): Promise<AvailableColumnSourcesResponse> => {
  try {
    const url = `${API_DOMAIN}/ideas/columns/available-sources`;
    const response = await axios.get<AvailableColumnSourcesResponse>(url, {
      params: { pageKey },
    });
    return response.data;
  } catch (e: any) {
    console.error("Error getting available column sources:", e);
    throw e;
  }
};

export interface AddIdeaPageColumnBody {
  accessor: string;
  title: string;
  renderComponent: string;
  source: string;
  dataType?: string;
  readOnly: boolean;
}

export const addIdeaPageColumn = async (
  auth0: any,
  pageKey: string,
  body: AddIdeaPageColumnBody
): Promise<any> => {
  const encodedKey = encodeURIComponent(pageKey);
  const url = `${API_DOMAIN}/ideas/pages/${encodedKey}/columns/add`;
  const response = await axios.post(url, body);
  return response.data;
};